'use client';

import { type PinnedFactiii } from '@shared/all/domains/factiiis';
import { DeploymentLayout } from '@shared/all/domains/spaces';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import useAuth from '@shared/utils/hooks/useAuth';
import { useConversations } from '@shared/utils/hooks/useConversations';
import useDefaultFactiiis from '@shared/utils/hooks/useDefaultFactiiis';
import useFactiiis from '@shared/utils/hooks/useFactiiis';
import useNavigator from '@shared/utils/hooks/useNavigator';
import useRole from '@shared/utils/hooks/useRole';
import useUser from '@shared/utils/hooks/useUser';
import { Logo } from '@shared/utils/icons';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { ArrowLeft, ArrowRight, Bell, Gamepad2, HelpCircle, Loader2, MapPin, MessageCircle, Search, ShieldCheck, ShieldUser, Users, Zap } from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { UNREAD_MESSAGES_KEY } from '../../constants/queryKeys';
import useIsElectron from '../../hooks/useIsElectron';
import { useRunners } from '../../hooks/useRunners';
import { useSearchScope } from '../../hooks/useSearchScope';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client, service } from '../../utils/trpc';
import { useChatWorkspace } from '../pages/chat/ChatWorkspaceContext';
import Avatar from './Avatar';
import SearchBar from './SearchBar';
import UserDropdown from './UserDropdown';

// Empty gaps between header items inherit `drag` from the <header>; only the
// interactive elements opt out so the rest of the bar is grabbable like a titlebar.
const noDragStyle = {
  WebkitAppRegion: 'no-drag'
} as React.CSSProperties;

// Lazy load modals - these are rarely opened but were adding ~1MB to initial bundle
const NewPostModal = dynamic(() => import('../posts/NewPost/NewPostModal').then(mod => mod.NewPostModal), {
  ssr: false
});
const AccountVerificationModal = dynamic(() => import('./AccountVerificationModal'), {
  ssr: false
});

// Renders null unless a signed-in owner has a runner behind npm's latest, so
// it stays out of the chunk every anonymous visitor downloads.
const RunnerUpdateBanner = dynamic(() => import('./RunnerUpdateBanner'), {
  ssr: false
});

// Feature tours, launched on demand from the header "?" for the current page.
// Lazy so their fixture/board payloads stay out of the base bundle.
const SocialTour = dynamic(() => import('../onboarding/SocialTour'), {
  ssr: false
});
const ResearchTour = dynamic(() => import('../onboarding/ResearchTour'), {
  ssr: false
});
const DevelopmentDemo = dynamic(() => import('../onboarding/DevelopmentDemo'), {
  ssr: false
});
type HelpTour = 'social' | 'research' | 'development';
type Props = {
  isHeaderVisible: boolean;
  setIsHeaderVisible: (value: boolean) => void;
  disableAutoHide?: boolean;
};
const Header = ({
  isHeaderVisible,
  setIsHeaderVisible,
  disableAutoHide = false
}: Props) => {
  const isElectron = useIsElectron();
  const {
    currentUser
  } = useUser();
  const {
    isAdmin,
    isModerator
  } = useRole(client);
  const defaultFactiiiIds = useDefaultFactiiis(client);
  const {
    data: pendingNotificationsCount = 0
  } = useQuery({
    queryKey: ['notifications', 'unreadCount'],
    queryFn: async () => {
      const [, d] = await client.notifications.unreadCount.query();
      return d?.count ?? 0;
    },
    enabled: currentUser.isAuthenticated === true
  });
  const {
    getFactiiiId
  } = useFactiiis(client);
  // Live runner jobs, badged on the spaces icon — that icon is how you reach
  // the Running jobs panel on narrow screens, where the left rail is hidden.
  const {
    sessions: runnerSessions
  } = useRunners();
  const {
    setLogoClicked
  } = useNavigator();
  const {
    openLogoutModal,
    openLoginModal,
    openSignupModal,
    openLoginForAddAccount
  } = useContext(AuthWidgetContext);
  const {
    conversationsState
  } = useConversations();
  // The chat panel itself lives in Main; the header only opens and closes it.
  const chatWorkspace = useChatWorkspace();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const navIconsRef = useRef<HTMLDivElement>(null);
  // Width the Games link occupies (icon + its gap), captured while it is still
  // in the bar so we know what re-showing it would cost once it's hidden.
  const gamesNavWidthRef = useRef(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [pinnedFactiii, setPinnedFactiii] = useState<PinnedFactiii | undefined>(undefined);
  const [prevScrollY, setPrevScrollY] = useState(0);
  const [newPostModalOpen, setNewPostModalOpen] = useState(false);
  const [switchingToId, setSwitchingToId] = useState<number | null>(null);
  // Games is the first icon to give up its slot when the bar runs out of room;
  // it moves into the avatar dropdown instead of squeezing the whole row.
  const [gamesInNav, setGamesInNav] = useState(true);
  const {
    switchTo,
    removeAccount
  } = useAuth({
    client,
    service
  });
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // Mobile shows a live search input on /search and on pages that scope the
  // search to themselves; elsewhere the strip keeps a search icon that
  // navigates to /search.
  const isSearchPage = pathname === '/search';
  const {
    scope: searchScope
  } = useSearchScope();

  // The "?" help button launches the tour for whatever feature the current page
  // shows. Home is the social feed on the multi-space app, a board on single-
  // space deployments.
  const [activeTour, setActiveTour] = useState<HelpTour | null>(null);
  const isSpaceLayout = spaceConfig.layoutType === DeploymentLayout.Space;
  const helpTour: HelpTour | null = pathname === '/search' ? 'research' : pathname === '/spaces' ? 'development' : pathname === '/' || pathname === '/home' ? isSpaceLayout ? 'development' : 'social' : null;
  const sessionsQuery = useQuery({
    queryKey: ['sessions', 'accounts'],
    queryFn: async () => {
      const [error, data] = await client.sessions.accounts.query();
      if (error) return [];
      return data;
    },
    enabled: currentUser.isAuthenticated === true
  });
  const storedAccounts = (sessionsQuery.data ?? []).filter(s => !s.isActive);

  // The server counts every conversation, not just the page of ten the
  // conversations query has loaded, so the badge is right on first paint.
  // Reading a chat and receiving one both invalidate this key.
  const {
    data: unreadMessagesCount = 0
  } = useQuery({
    queryKey: UNREAD_MESSAGES_KEY,
    queryFn: async () => {
      const [, d_0] = await client.messages.unreadCount.query();
      return d_0?.count ?? 0;
    },
    enabled: currentUser.isAuthenticated === true
  });

  // Open the composer from `?compose=<factiiiId>`. The dropdown navigates to the
  // board with this param (the modal state can't survive navigation since Header
  // remounts per page), so the destination Header opens it, then clears the param.
  const composeParam = searchParams?.get('compose');
  useEffect(() => {
    if (!composeParam) return;
    const factiiiId = Number(composeParam);
    if (!factiiiId) return;
    let ignore = false;
    getFactiiiId(factiiiId).then(factiii => {
      if (ignore) return;
      if (factiii) {
        setPinnedFactiii(factiii);
        setNewPostModalOpen(true);
      }
      // Clear the param only after resolving so the modal isn't suppressed by
      // this effect's cleanup, and so back/refresh doesn't reopen the composer.
      const params = new URLSearchParams(searchParams?.toString());
      params.delete('compose');
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname ?? '', {
        scroll: false
      });
    });
    return () => {
      ignore = true;
    };
  }, [composeParam]);

  // Use scroll container when in AppLayout (data-scroll-lock-root), else window.
  // disableAutoHide: fullWidth pages (Products, Admin, Map, …) pin the header
  // so the MultiPage sticky header can't visually replace it during navigation.
  useEffect(() => {
    if (disableAutoHide) {
      setIsHeaderVisible(true);
      return;
    }
    const el = document.querySelector<HTMLElement>('[data-scroll-lock-root]');
    const handleScroll = () => {
      const currentScrollY = el ? el.scrollTop : window.scrollY;
      const atBottom = el ? el.scrollTop + el.clientHeight >= el.scrollHeight : window.innerHeight + currentScrollY >= document.body.offsetHeight;
      if (atBottom) return;
      if (prevScrollY < currentScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      setPrevScrollY(currentScrollY);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
      return () => el.removeEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollY, setIsHeaderVisible, disableAutoHide]);
  const handleQuickSwitch = async (sessionId: number) => {
    setSwitchingToId(sessionId);
    try {
      const success = await switchTo(sessionId);
      if (success) {
        setDropdownOpen(false);
        router.replace('/');
      }
    } finally {
      setSwitchingToId(null);
    }
  };
  const handleRemoveAccount = async (sessionId_0: number) => {
    const success = await removeAccount(sessionId_0);
    if (success) sessionsQuery.refetch();
  };
  const handleMessageIcon = () => {
    if (!conversationsState.conversationListOpen) {
      if (!currentUser.isAuthenticated) return openLoginModal();
    }
    // Close other dropdowns when opening conversations
    setDropdownOpen(false);
    chatWorkspace.toggleSidebar();
  };

  // Drop Games out of the icon bar when it no longer fits, and put it back when
  // it does. The icons are `shrink-0`, so they keep their natural size instead
  // of silently squashing (which is what used to happen — every icon lost a
  // third of its width rather than one of them stepping aside).
  //
  // Content width is summed from the children rather than read off scrollWidth:
  // the row is `justify-end`, so overflow runs past the *start* edge, which
  // scrollWidth does not report. The row's own width comes from flex layout,
  // not its contents, so toggling Games can't change clientWidth — the observer
  // has no feedback loop, and the asymmetric re-show test below adds hysteresis.
  useEffect(() => {
    const row = navIconsRef.current;
    if (!row) return;
    const measure = () => {
      const items = Array.from(row.children) as HTMLElement[];
      // Below `md` this whole bar is display:none, so every width reads 0 —
      // measuring then would banish Games for a layout that isn't on screen.
      if (items.length === 0 || row.clientWidth === 0) return;
      const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
      const contentWidth = items.reduce((sum, el_0) => sum + el_0.offsetWidth, 0) + gap * (items.length - 1);
      if (gamesInNav) {
        const link = row.querySelector<HTMLElement>('[data-nav-games]');
        if (link) gamesNavWidthRef.current = link.offsetWidth + gap;
        if (contentWidth > row.clientWidth) setGamesInNav(false);
      } else if (contentWidth + gamesNavWidthRef.current <= row.clientWidth) {
        setGamesInNav(true);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [gamesInNav]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      // Check both dropdown refs
      if (dropdownRef.current && !dropdownRef.current.contains(target) && mobileDropdownRef.current && !mobileDropdownRef.current.contains(target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside as EventListener);
    document.addEventListener('touchstart', handleClickOutside as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as EventListener);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
    };
  }, []);

  // Mobile bottom rail, ordered to match the desktop bar's left-to-right run.
  // `href: undefined` marks the one action that toggles a panel instead of
  // navigating; `badge` is a getter so counts stay live per render.
  const mobileRailActions: Array<{
    key: string;
    title: string;
    Icon: typeof MapPin;
    href?: string;
    requiresAuth?: boolean;
    badge?: () => number;
  }> = [{
    key: 'map',
    title: 'Map',
    Icon: MapPin,
    href: '/map'
  }, {
    key: 'games',
    title: 'Games',
    Icon: Gamepad2,
    href: '/games'
  }, {
    key: 'trons',
    title: 'Trons',
    Icon: Zap,
    href: '/products/my'
  }, {
    key: 'notifications',
    title: 'Notifications',
    Icon: Bell,
    href: '/notifications',
    requiresAuth: true,
    badge: () => currentUser.isAuthenticated ? pendingNotificationsCount : 0
  }, {
    key: 'messages',
    title: 'Messages',
    Icon: MessageCircle,
    badge: () => conversationsState.conversationListOpen ? 0 : unreadMessagesCount
  }];
  return <>
      <header
    // Measured by useHeaderHeight so the layout offsets content by the
    // header actually on screen, not by a constant.
    data-app-header className={`fixed top-0 z-50 mb-1 hidden w-full flex-col border-b border-ds-border-subtle
           bg-background-primary/80 backdrop-blur-md transition-all
            duration-300 ease-in-out md:flex ${isHeaderVisible || isElectron ? 'translate-y-0' : '-translate-y-full'}`} style={isElectron ? {
      WebkitAppRegion: 'drag'
    } as React.CSSProperties : undefined}>
        {/* The bar itself. Its own row, so the update banner below is a full
            -width row rather than another item competing for space in it. */}
        <div className={`flex w-full justify-between px-4 ${isElectron ? 'pb-0.5 pt-3' : 'py-1.5'}`}>
          {/* Sizes to its own icons. With w-full it claimed a 300px basis it
            rarely needed, and the search bar gave up that width instead. */}
          <div className="flex max-w-[300px] shrink-0 items-center justify-start gap-x-6">
            {/* Traffic light spacer + logo + nav buttons for Electron */}
            {isElectron && <>
                <div className="w-[68px] shrink-0" />
                <Link href="/" onClick={() => {
              setLogoClicked(true);
              chatWorkspace.minimizeAll();
            }} className="inline-block rounded-full transition-transform duration-200 hover:scale-105" style={noDragStyle}>
                  {spaceConfig.space.slug === 'factiii' ? <Logo height={28} rounded /> : <Avatar spaceAvatarKey={spaceConfig.space.avatarKey} size={28} />}
                </Link>
                <button onClick={() => window.electronAPI?.goBack()} className="flex size-6 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary" title="Go back" style={noDragStyle}>
                  <ArrowLeft size={14} />
                </button>
                <button onClick={() => window.electronAPI?.goForward()} className="flex size-6 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-background-light hover:text-textPrimary" title="Go forward" style={noDragStyle}>
                  <ArrowRight size={14} />
                </button>
              </>}
            {!isElectron && <Link href="/" onClick={() => {
            setLogoClicked(true);
            chatWorkspace.minimizeAll();
          }} className="inline-block rounded-full transition-transform duration-200 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background-primary">
                {spaceConfig.space.slug === 'factiii' ? <Logo height={32} rounded /> : <Avatar spaceAvatarKey={spaceConfig.space.avatarKey} size={32} />}
              </Link>}
            <div className="block md:hidden">
              <Link href="/spaces" className="ml-4 flex">
                <button title={runnerSessions.length ? `Spaces — ${runnerSessions.length} running job${runnerSessions.length === 1 ? '' : 's'}` : 'spaces'} className="relative rounded-full p-2 transition-all duration-200 hover:scale-110 hover:bg-background-light">
                  <Users height={20} width={20} />
                  {runnerSessions.length > 0 && <span className="absolute right-0 top-0 flex size-4 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                      {runnerSessions.length > 9 ? '∞' : runnerSessions.length}
                    </span>}
                </button>
              </Link>
            </div>
            {isAdmin && <Link href="/admin" className="rounded-full p-2 no-underline transition-all duration-200 hover:scale-110 hover:bg-background-light" title="Admin" style={isElectron ? noDragStyle : undefined}>
                <ShieldCheck size={22} />
              </Link>}
            {isModerator && <Link href="/moderation/all" className="rounded-full p-2 no-underline transition-all duration-200 hover:scale-110 hover:bg-background-light hover:underline" style={isElectron ? noDragStyle : undefined}>
                <ShieldUser />
              </Link>}
          </div>
          <div style={isElectron ? {
          WebkitAppRegion: 'no-drag'
        } as React.CSSProperties : undefined}
        // A real floor, not min-w-0: both sibling groups carry w-full, so an
        // unfloored bar loses every pixel to them and collapses to the icon.
        // The bar's own contents shrink correctly now (see SearchBar), so the
        // floor no longer causes the overflow it used to.
        // Centred on the VIEWPORT, not on the space the side groups leave.
        // In flow it can't be: the two groups reserve different widths (left
        // sizes to its icons, right reserves up to 380px), so any in-flow
        // centring is offset by half that difference. Taking the bar out of
        // flow at xl+ is the only way to centre it without equalising those
        // reservations, which would claw back the width the left group was
        // deliberately given up (see its comment).
        //
        // `wide` (1480px) and up, and the width is clamped so it can never
        // reach a side group: the widest is 380px, so subtracting 800px
        // leaves 400px of clearance on each side at any window size. Below
        // it the bar stays in flow, where the existing shrink/overflow
        // tuning still applies — that is also why the header keeps
        // `justify-between`: with the bar out of flow the two groups simply
        // sit at the edges.
        //
        // 1480px rather than xl (1280px) because that is where the two
        // widths meet: `calc(100% - 800px)` only reaches the 680px cap at
        // 1480. Switching at xl dropped the bar from ~650px in flow to
        // 480px centred, and the desktop window sits in exactly that band —
        // the bar looked truncated with empty header either side of it.
        //
        // No `relative` needed on the header: it is `fixed`, which already
        // establishes the containing block.
        className="min-w-[200px] max-w-[680px] flex-1 wide:absolute wide:left-1/2 wide:w-[min(680px,calc(100%-800px))] wide:max-w-none wide:flex-none wide:-translate-x-1/2">
            <Suspense fallback={<div className="h-11 w-full animate-pulse rounded-full bg-background-secondary" />}>
              <SearchBar className="w-full" />
            </Suspense>
          </div>
          <div ref={navIconsRef}
        // Cap sized to fit the full icon set at its natural width; the old
        // 300px could not, so Games never had room at any window size.
        // min-w-0 is load-bearing: a flex item defaults to min-width:auto, so
        // without it the row is sized by its contents and clientWidth can
        // never fall below contentWidth — the overflow test above would never
        // fire. The cap is sized to fit the whole icon set at natural width.
        className={`flex w-full min-w-0 max-w-[380px] items-center justify-end ${isElectron ? 'gap-x-4' : 'gap-x-6'}`}>
            <Link href={'/map'} className={`relative flex shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-background-light ${isElectron ? 'size-8' : 'size-10'}`} style={isElectron ? noDragStyle : undefined}>
              <MapPin height={isElectron ? 15 : 18} width={isElectron ? 15 : 18} />
            </Link>
            {gamesInNav && <Link href="/games" title="Games" data-nav-games className={`relative flex shrink-0 items-center justify-center rounded-full text-textPrimary transition-all duration-200 hover:scale-110 hover:bg-hover ${isElectron ? 'size-8' : 'size-10'}`} style={isElectron ? noDragStyle : undefined}>
                <Gamepad2 size={isElectron ? 15 : 18} />
              </Link>}
            <Link href="/products/my" title="Trons" className={`relative flex shrink-0 items-center justify-center rounded-full text-textPrimary transition-all duration-200 hover:scale-110 hover:bg-hover ${isElectron ? 'size-8' : 'size-10'}`} style={isElectron ? noDragStyle : undefined}>
              <Zap size={isElectron ? 15 : 18} />
            </Link>
            {false && helpTour && <button onClick={() => setActiveTour(helpTour)} title="How to use this page" className={`relative flex items-center justify-center rounded-full text-textPrimary transition-all duration-200 hover:scale-110 hover:bg-hover ${isElectron ? 'size-8' : 'size-10'}`} style={isElectron ? noDragStyle : undefined}>
                <HelpCircle size={isElectron ? 15 : 18} />
              </button>}
            {verificationModalOpen && <AccountVerificationModal closeHandler={() => setVerificationModalOpen(false)} />}
            <Link href="/notifications" onClick={e => {
            if (!currentUser.isAuthenticated) {
              e.preventDefault();
              return openLoginModal();
            }
          }} className={`relative flex shrink-0 items-center justify-center rounded-full text-white transition-all duration-200 hover:scale-110 hover:bg-background-light ${isElectron ? 'size-8' : 'size-10'}`} style={isElectron ? noDragStyle : undefined}>
              <Bell height={isElectron ? 15 : 18} width={isElectron ? 15 : 18} />
              {pendingNotificationsCount ? <span className="absolute right-1 top-1 flex size-4 animate-pulse items-center justify-center rounded-full bg-destructive text-xs font-semibold text-destructive-foreground">
                  {pendingNotificationsCount > 99 ? '\u221E' : pendingNotificationsCount}
                </span> : null}
            </Link>
            <button onClick={handleMessageIcon} className={classNames(`relative shrink-0 rounded-full transition-all duration-200 hover:bg-background-light ${isElectron ? 'h-8 p-1.5' : 'h-10 p-2'}`, conversationsState.conversationListOpen && 'bg-hover')} title="Messages" style={isElectron ? noDragStyle : undefined}>
              <MessageCircle height={isElectron ? 15 : 18} width={isElectron ? 15 : 18} className={conversationsState.conversationListOpen ? 'fill-current' : ''} />
              {unreadMessagesCount > 0 && !conversationsState.conversationListOpen && <span className="absolute right-1 top-1 flex size-4 animate-pulse items-center justify-center rounded-full bg-destructive text-xs font-semibold text-destructive-foreground">
                    {unreadMessagesCount > 99 ? '\u221E' : unreadMessagesCount}
                  </span>}
            </button>
            <div className="relative shrink-0" ref={dropdownRef} style={isElectron ? noDragStyle : undefined}>
              <div className="flex w-full cursor-pointer items-center rounded-full border-2 border-solid border-transparent transition-all duration-200 hover:border-ds-border-hover" onClick={() => {
              // Close other panels when opening profile dropdown
              if (conversationsState.conversationListOpen) {
                chatWorkspace.closeSidebar();
              }
              setDropdownOpen(prev => !prev);
            }}>
                {currentUser && <Avatar avatarKey={currentUser.avatarKey ?? null} robohash={currentUser.robohash ?? ''} isBot={currentUser.tag === 'BOT'} userId={currentUser.id} size={isElectron ? 28 : 35} />}
              </div>
              {dropdownOpen && <UserDropdown setDropdownOpen={value => setDropdownOpen(value)} isAdmin={isAdmin} showGames={!gamesInNav} openNewPostModal={() => {
              if (!defaultFactiiiIds.bug) return;
              // Open the factiii board with a compose param so the destination
              // Header opens the composer pinned to the bug column.
              router.push(`/s/factiii?view=board&compose=${defaultFactiiiIds.bug}`);
            }} openFeatureRequestModal={() => {
              if (!defaultFactiiiIds.featureRequest) return;
              // Open the factiii board with a compose param so the destination
              // Header opens the composer pinned to the feature column.
              router.push(`/s/factiii?view=board&compose=${defaultFactiiiIds.featureRequest}`);
            }} openLogoutModal={() => openLogoutModal()} openLoginModal={() => openLoginModal()} openSignupModal={() => openSignupModal()} openVerificationModal={() => setVerificationModalOpen(true)} openLoginForAddAccount={openLoginForAddAccount} storedAccounts={storedAccounts} onQuickSwitch={handleQuickSwitch} onRemoveAccount={handleRemoveAccount} switchingToId={switchingToId} />}
            </div>
          </div>
        </div>
        {/* Last row of the header, so `useHeaderHeight` measures it and every
            page offsets past it instead of the banner covering the page. */}
        <RunnerUpdateBanner />
      </header>
      <section
    // Two rows wherever the search strip renders below; measured so the
    // layout pushes the page past it. See useHeaderHeight.
    data-app-header className={`fixed top-0 z-50 flex w-full flex-col border-b border-solid border-ds-border-subtle bg-background-primary/80 backdrop-blur-md transition-transform duration-300 md:hidden ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="flex w-full items-center justify-between p-2">
          <Link href="/" onClick={() => chatWorkspace.minimizeAll()} className="inline-block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-border-standard focus-visible:ring-offset-2 focus-visible:ring-offset-background-primary">
            {spaceConfig.space.slug === 'factiii' ? <Logo height={32} rounded /> : <Avatar spaceAvatarKey={spaceConfig.space.avatarKey} />}
          </Link>
          {!(spaceConfig.layoutType === DeploymentLayout.Space) && <Link href="/spaces">
              <button title={runnerSessions.length ? `Spaces — ${runnerSessions.length} running job${runnerSessions.length === 1 ? '' : 's'}` : 'spaces'} className="relative flex size-10 items-center justify-center border-none bg-transparent align-middle">
                <Users height={20} width={20} />
                {runnerSessions.length > 0 && <span className="absolute right-0 top-0 flex size-4 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                    {runnerSessions.length > 9 ? '∞' : runnerSessions.length}
                  </span>}
              </button>
            </Link>}
          {/* Scoped pages render their own bar in the strip below, so the
              shortcut to global /search would sit right on top of it. */}
          {!isSearchPage && !searchScope && <Link href="/search" className="flex size-10 items-center justify-center align-middle" title="search">
              <Search height={20} width={20} />
            </Link>}
          {false && helpTour && <button onClick={() => setActiveTour(helpTour)} title="How to use this page" className="relative flex size-10 items-center justify-center border-none bg-transparent align-middle">
              <HelpCircle height={18} width={18} />
            </button>}
          {/* Mobile's only way into chat. The docked panel used to carry its
              own launcher; the workspace replaced it, so the strip has to
              hold one. */}
          <button onClick={handleMessageIcon} title="Messages" className="relative flex size-10 items-center justify-center border-none bg-transparent align-middle">
            <MessageCircle height={20} width={20} className={conversationsState.conversationListOpen ? 'fill-current' : ''} />
            {unreadMessagesCount > 0 && !conversationsState.conversationListOpen && <span className="absolute right-0 top-0 flex size-4 items-center justify-center rounded-full bg-destructive text-xs font-semibold text-destructive-foreground">
                  {unreadMessagesCount > 99 ? '∞' : unreadMessagesCount}
                </span>}
          </button>
          <div className="relative flex size-10 items-center justify-center border-none bg-transparent align-middle" ref={mobileDropdownRef}>
            <div onClick={() => {
            if (conversationsState.conversationListOpen) {
              chatWorkspace.closeSidebar();
            }
            setDropdownOpen(prev_0 => !prev_0);
          }}>
              {currentUser ? <Avatar avatarKey={currentUser.avatarKey} robohash={currentUser.robohash} isBot={currentUser.tag === 'BOT'} userId={currentUser.id} size={30} /> : <div className="absolute left-0 top-0 flex size-[30px] items-center justify-center">
                  <Loader2 className="size-4 animate-spin text-textSecondary" />
                </div>}
            </div>
            {dropdownOpen && <UserDropdown setDropdownOpen={(value_0: boolean) => setDropdownOpen(value_0)} isAdmin={isAdmin} openNewPostModal={() => {
            if (!defaultFactiiiIds.bug) return;
            // Open the factiii board with a compose param so the destination
            // Header opens the composer pinned to the bug column.
            router.push(`/s/factiii?view=board&compose=${defaultFactiiiIds.bug}`);
          }} openFeatureRequestModal={() => {
            if (!defaultFactiiiIds.featureRequest) return;
            // Open the factiii board with a compose param so the destination
            // Header opens the composer pinned to the feature column.
            router.push(`/s/factiii?view=board&compose=${defaultFactiiiIds.featureRequest}`);
          }} openLogoutModal={() => openLogoutModal()} openLoginModal={() => openLoginModal()} openSignupModal={() => openSignupModal()} openVerificationModal={() => setVerificationModalOpen(true)} openLoginForAddAccount={openLoginForAddAccount} storedAccounts={storedAccounts} onQuickSwitch={handleQuickSwitch} onRemoveAccount={handleRemoveAccount} switchingToId={switchingToId} />}
          </div>
        </div>
        {/* Mobile search input — on /search and on any page that registered a
            search scope, the two cases where SearchBar renders a live input.
            Everywhere else the strip keeps a search icon (above) that navigates
            here, so we don't render a redundant full-width bar. */}
        {(isSearchPage || searchScope) && <div className="px-2 pb-2">
            <Suspense fallback={<div className="h-11 w-full animate-pulse rounded-full bg-background-secondary" />}>
              <SearchBar className="w-full" />
            </Suspense>
          </div>}
        {/* Same row on mobile: the header is what the layout measures. */}
        <RunnerUpdateBanner />
      </section>
      {/* Mobile action rail. These used to sit across the top strip, which was
          both noisy and out of thumb reach. Stacked bottom-right instead, in
          the same order the desktop bar uses left-to-right, so Messages lands
          closest to the thumb. */}
      <nav aria-label="Quick actions" className="fixed bottom-3 right-3 z-nav flex flex-col items-center gap-2 md:hidden">
        {mobileRailActions.map(action => {
        const badge = action.badge?.();
        const content = <>
              <action.Icon height={20} width={20} />
              {badge ? <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-semibold text-destructive-foreground">
                  {badge > 99 ? '∞' : badge}
                </span> : null}
            </>;
        const className = 'relative flex size-11 items-center justify-center rounded-full border border-ds-border-subtle bg-background-secondary/90 text-textPrimary shadow-lg backdrop-blur-md transition-transform duration-200 active:scale-95';
        return action.href ? <Link key={action.key} href={action.href} title={action.title} aria-label={action.title} className={className} onClick={e_0 => {
          if (action.requiresAuth && !currentUser.isAuthenticated) {
            e_0.preventDefault();
            openLoginModal();
          }
        }}>
              {content}
            </Link> : <button key={action.key} type="button" title={action.title} aria-label={action.title} onClick={handleMessageIcon} className={classNames(className, conversationsState.conversationListOpen && 'bg-hover')}>
              {content}
            </button>;
      })}
      </nav>
      {currentUser.isAuthenticated && <>
          {/* The chat panel itself is mounted in Main, so it survives page
              navigation and shares one state with the /chat full screen. */}
          {newPostModalOpen && <NewPostModal closeHandler={() => setNewPostModalOpen(false)} pinnedFactiii={pinnedFactiii}
      // This composer only opens for Report Bug / Feature Request, whose
      // factiiis live on the factiii space board — post into that space.
      spaceSlug="factiii" />}
        </>}
      {activeTour === 'social' && <SocialTour onClose={() => setActiveTour(null)} />}
      {activeTour === 'research' && <ResearchTour onClose={() => setActiveTour(null)} />}
      {activeTour === 'development' && <DevelopmentDemo onClose={() => setActiveTour(null)} />}
    </>;
};
export default Header;